import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personalizado para efectos de scroll
 * Convierte la funcionalidad de scroll del JavaScript original a React hooks
 */
export const useScrollEffects = () => {
  const [scrollY, setScrollY] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);

  // Thresholds para los efectos
  const scrollThreshold = 50;
  const hideThreshold = 300;

  // Manejar scroll con throttling optimizado
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY || document.documentElement.scrollTop;
    
    setScrollY(currentScrollY);
    
    // Determinar si el header debe mostrar efecto de scroll
    setIsHeaderScrolled(currentScrollY > scrollThreshold);
    
    // Determinar si el header debe ocultarse/mostrarse
    if (currentScrollY > lastScrollY && currentScrollY > hideThreshold) {
      // Scrolling hacia abajo y se pasó el threshold - ocultar header
      setIsHeaderVisible(false);
    } else {
      // Scrolling hacia arriba o no se pasó el threshold - mostrar header
      setIsHeaderVisible(true);
    }
    
    setLastScrollY(currentScrollY <= 0 ? 0 : currentScrollY);
  }, [lastScrollY, hideThreshold, scrollThreshold]);

  // Efecto para configurar el listener de scroll
  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Agregar listener
    window.addEventListener('scroll', onScroll, { passive: true });

    // Limpiar listener
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [handleScroll]);

  // Función para scroll suave a un elemento
  const scrollToElement = useCallback((elementId: string, offset = 20) => {
    const element = document.getElementById(elementId.replace('#', ''));
    if (!element) return;

    const headerHeight = document.getElementById('header')?.offsetHeight || 0;
    // --seasonal-banner-offset: banner estacional fixed (hoy ninguno; el
    // último fue HalloweenBanner, ver git log) empuja el header fijo hacia abajo. Sin el
    // banner montado, la variable no existe y parseInt('') es NaN → || 0
    // hace que esto sea un no-op inerte.
    const bannerOffset = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--seasonal-banner-offset'),
      10
    ) || 0;
    const targetPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight - bannerOffset - offset;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  }, []);

  // Función para scroll suave al inicio
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  return {
    scrollY,
    lastScrollY,
    isHeaderVisible,
    isHeaderScrolled,
    scrollToElement,
    scrollToTop
  };
};